<template>
	<div class="catalog">
		
<FormKitLazyProvider config-file="true" :default-config="false">
<div class="catalog-sort">
			<CatalogTags :tags />
			<FormKit v-model="sort" type="selectC" :value="optionsArr?.[0].value" :options="optionsArr" />
		</div>
		<div class="catalog-content">
			<div class="catalog-wrapper">
				<SubcategoryCard v-for="card in paginationData" :key="card.id" :card />
				<div v-if="totalPages / pageCount > 1" class="catalog-navigation-box col-span-full">
					<CatalogPagination v-show="false" v-model="paginationPage" :total="totalPages" :pageCount />
					<button v-if="paginationPage < totalPages / pageCount" class="btn" :class="{ 'btn-loaded': status === 'pending' }" @click="showMore" :disabled="status === 'pending'">{{ t('interface.show_more') }}</button>
				</div>
				<slot name="content-end" />
			</div>
		</div>
</FormKitLazyProvider>

	</div>
</template>

<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import { useCategories } from '~/composables/categories/useCategories';
import SubcategoryCard from './SubcategoryCard.vue';
const props = defineProps<{ asyncKey: string }>()

const { getSubCategories } = useCategories()

const { t } = useI18n()

const tags = [
	{
		id: 1,
		name: t('home.allServices'),
		slug: '/services'
	},
	{
		id: 2,
		name: t('home.allGames'),
		slug: '/games'
	}
]

const optionsArr = [
	{
		label: t('interface.sort_az'),
		value: 'name:asc',
	},
	{
		label: t('interface.sort_za'),
		value: 'name:desc',
	},
];

const { pageCount, paginationData, paginationPage, showMore, sort, status, totalPages } = await useComplexPagination(`subcategory-key-${props.asyncKey}`, async (a) => {
	const data = await getSubCategories(props.asyncKey, { sort: optionsArr?.[0].value, ...a, limit: 20 })
	return data?.categories ?? null
})
</script>

<style lang="scss" scoped>
.tags-container {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
}

.tag-item {
	display: flex;
	align-items: center;
	padding: 13px 32px 13px 32px;
	border-radius: 29px;
	background-color: #DA47271A;
	color: #DA4727;
	cursor: pointer;
	font-size: 18px;
	font-weight: 400;
	line-height: 80%;
	letter-spacing: -0.03em;
	transition: all .3s ease;

	&.router-link-active,
	&:hover {
		background-color: #DA4727;
		color: white;
		font-weight: 700;
	}

}

@media (max-width:1023px) {
	.tags-container {
		gap: 6px;
	}

	.tag-item {
		padding: 10px 22px;
		font-size: 16px;
	}
}

.catalog {
	display: flex;
	flex-direction: column;
	gap: 32px;
}

.catalog-sort {
	display: flex;
	justify-content: space-between;
	gap: 12px;

	:deep(.formkit-outer) {
		width: 302px;

		.formkit-inner {
			border: 1.50px solid rgba(218, 71, 39, 0.3);
			border-radius: 29px;
		}

		.formkit-input {
			font-weight: 400;
			font-size: 18px;
			line-height: 22px;
			letter-spacing: -0.03em;
			color: #090630;
			padding: 8px 12px 6px 14px;

			.formkit-select-text {
				line-height: 22px;
				height: 22px;
			}
		}

		.formkit-dropdown-wrapper {
			box-shadow: 0 4px 6px 0 rgba(77, 24, 13, 0.1);
			background: #fff;
			border: 1px solid rgba(218, 71, 39, 0.2);
		}

		.formkit-list-item {
			transition: background, color 0.3s ease;

			&-select,
			&:hover {
				background: rgba(218, 72, 39, 0.2);
				color: #da4727;
			}
		}
	}
}

.catalog-select {
	width: 302px;
}

.catalog-content {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 20px;
	z-index: 0;

	:deep(.modal-overlay) {
		&.filter-hidden {
			display: none;
		}

		.modal-content {
			border-radius: 0;
			padding: 0;
			background: none;
		}
	}
}

.catalog-wrapper {
	display: grid;
	width: 100%;
	grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
	gap: 16px;

	.catalog-navigation-box {
		display: flex;
		justify-content: center;
		gap: 56px;
		margin-top: 12px;

		.btn {
			background: #eeedf0;
			border-radius: 100px;
			padding: 15px 20px;
			font-weight: 500;
			font-size: 20px;
			line-height: 100%;
			align-self: center;
			min-width: 320px;
			width: 50%;
			letter-spacing: -0.01em;
			color: #090630;
			justify-content: center;
			transition: background, color, .3s ease;

			&:hover {
				background: #da4727;
				color: #fff;
			}
		}
	}
}

.button-box {
	display: flex;
	gap: 8px;

	.btn {
		width: calc(50% - 4px);

		img {
			width: 20px;
			height: 20px;
		}
	}
}

@media (max-width:1023px) {
	.catalog {
		gap: 24px;
	}

	.catalog-content {
		grid-template-columns: 1fr;
	}

	.catalog-wrapper {
		grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
		gap: 12px 8px;

		.catalog-navigation-box {
			margin-top: 6px;
			flex-direction: column;
			gap: 12px;

			.btn {
				width: 100%;
				padding: 14px 12px;
				font-size: 16px;
			}
		}
	}

	.catalog-sort {
		flex-wrap: wrap;
		gap: 8px;

		:deep(.formkit-outer) {
			max-width: none;
			width: 133px;
			flex: 1;

			.formkit-input {
				font-size: 15px;
				line-height: 20px;
				padding: 3px 32px 3px 12px;

				.formkit-select-text {
					font-size: 12px;
					line-height: 20px;
					height: 20px;
				}
			}

			.formkit-select-icon {
				right: 12px;
				height: 14px;
				width: 14px;
			}

			.formkit-list-item {
				font-size: 15px;
			}

			.formkit-dropdown-wrapper {
				width: max-content;
				right: 0;
				left: auto;
				max-width: 300px;
			}
		}
	}

	.filter-btn {
		font-weight: 700;
		font-size: 14px;
		line-height: 80%;
		letter-spacing: -0.03em;
		flex: 1;
		align-items: center;
		padding: 7px 12px;
		flex: 1
	}
}

@media (min-width: 1024px) {

	.filter-btn {
		display: none;
	}

	.catalog-content {

		:deep(.modal-overlay) {
			z-index: auto;
			background: none;
			position: static;
			overflow: hidden;

			&.filter-hidden {
				display: block;
			}

			.modal-wrapper {
				padding: 0;
			}

			.modal-content {
				border-radius: 0;
				padding: 0;
			}
		}

	}
}
</style>